import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Share2, Copy, Loader2, ExternalLink } from "lucide-react";

export default function ShareButton({ kind, params, title }) {
  const { canEdit } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null);

  const create = async () => {
    if (!canEdit || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/share", { kind, params, title });
      data.url = `${window.location.origin}${data.path}`;
      setShare(data);
      setOpen(true);
      try { await navigator.clipboard.writeText(data.url); toast.success("Public link copied"); } catch { /* clipboard blocked */ }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(share.url); } catch { /* ignore */ }
    toast.success("Link copied");
  };

  if (!canEdit) return null;
  return (
    <>
      <Button variant="outline" size="sm" onClick={create} disabled={loading} data-testid={`share-${kind}-button`}>
        {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />} Share report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Public report link</DialogTitle>
            <DialogDescription>Anyone with this link can view a read-only snapshot of this report — no login required.</DialogDescription>
          </DialogHeader>
          {share && (
            <div className="space-y-3">
              <div className="text-sm font-medium">{share.title}</div>
              <div className="flex gap-2">
                <Input readOnly value={share.url} className="font-mono text-xs" data-testid="share-url-input" />
                <Button variant="outline" size="icon" onClick={copy} aria-label="Copy" data-testid="share-copy-button"><Copy className="h-4 w-4" /></Button>
                <Button asChild variant="outline" size="icon" aria-label="Open">
                  <a href={share.url} target="_blank" rel="noreferrer" data-testid="share-open-link"><ExternalLink className="h-4 w-4" /></a>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
